import React, { useState, useRef } from 'react';
import { Download, Copy, RotateCcw, Eye, EyeOff, Link as LinkIcon, Smartphone, Zap } from 'lucide-react';
import QRCode from 'react-qr-code';

export default function App() {
  const [inputValue, setInputValue] = useState('https://example.com');
  const [isUrl, setIsUrl] = useState(true);
  const [size, setSize] = useState(256);
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [showPreview, setShowPreview] = useState(true);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef();

  const generateQR = () => {
    // Reset any previous copy state
    setCopied(false);
  };

  const downloadQR = () => {
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = 'qr-code.png';
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = `image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inputValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const resetForm = () => {
    setInputValue('https://example.com');
    setIsUrl(true);
    setSize(256);
    setForegroundColor('#000000');
    setBackgroundColor('#ffffff');
    setShowPreview(true);
    setCopied(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Zap className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Генератор QR-кодов</h1>
                <p className="text-sm text-gray-600">Быстро и бесплатно создайте QR-код за секунды</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <Smartphone className="h-4 w-4" />
                <span>Сканируйте на смартфоне</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6 h-fit">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Создайте QR-код</h2>
              <p className="text-gray-600">Введите текст, URL или другую информацию для генерации QR-кода</p>
            </div>

            <div className="space-y-6">
              {/* Input Type Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => {
                    setIsUrl(true);
                    setInputValue('https://example.com');
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    isUrl
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <LinkIcon className="h-4 w-4" />
                    <span>Ссылка</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setIsUrl(false);
                    setInputValue('');
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    !isUrl
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>Текст</span>
                  </div>
                </button>
              </div>

              {/* Main Input */}
              <div>
                <label htmlFor="qr-input" className="block text-sm font-medium text-gray-700 mb-2">
                  {isUrl ? 'URL-адрес' : 'Текст'}
                </label>
                <div className="relative">
                  <input
                    id="qr-input"
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={isUrl ? 'https://example.com' : 'Введите текст...'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                    onKeyUp={(e) => e.key === 'Enter' && generateQR()}
                  />
                  {inputValue && (
                    <button
                      onClick={copyToClipboard}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                      title="Копировать в буфер обмена"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {copied && (
                  <div className="mt-2 text-sm text-green-600 flex items-center">
                    <span>Скопировано!</span>
                  </div>
                )}
              </div>

              {/* Size Slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">Размер: {size}px</label>
                  <span className="text-xs text-gray-500">Макс. 512px</span>
                </div>
                <input
                  type="range"
                  min="128"
                  max="512"
                  step="32"
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>

              {/* Color Pickers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Цвет QR-кода</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={foregroundColor}
                      onChange={(e) => setForegroundColor(e.target.value)}
                      className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">{foregroundColor}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Фон</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">{backgroundColor}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  onClick={generateQR}
                  className="flex-1 min-w-[120px] bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <Zap className="h-4 w-4" />
                  <span>Сгенерировать</span>
                </button>
                <button
                  onClick={downloadQR}
                  className="flex-1 min-w-[120px] bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Скачать</span>
                </button>
                <button
                  onClick={resetForm}
                  className="p-3 border border-gray-300 hover:bg-gray-50 text-gray-600 rounded-lg transition-colors"
                  title="Сбросить"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Предпросмотр QR-кода</h2>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title={showPreview ? 'Скрыть QR-код' : 'Показать QR-код'}
              >
                {showPreview ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            <div className="flex flex-col items-center justify-center min-h-[300px]">
              {showPreview ? (
                <div
                  ref={qrRef}
                  className="p-4 bg-gray-50 rounded-xl flex items-center justify-center"
                  style={{
                    width: `${size + 32}px`,
                    height: `${size + 32}px`,
                  }}
                >
                  <QRCode
                    value={inputValue || ' '}
                    size={size}
                    style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                    bgColor={backgroundColor}
                    fgColor={foregroundColor}
                    level="H"
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Eye className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">QR-код скрыт</h3>
                  <p className="text-gray-600">Нажмите на иконку 👁️ чтобы показать QR-код</p>
                </div>
              )}

              {inputValue && (
                <div className="mt-6 w-full max-w-xs">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-sm font-medium text-gray-900 mb-1">Содержимое QR-кода:</p>
                    <p className="text-xs text-gray-600 break-all">{inputValue}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Usage Tips */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="font-medium text-gray-900 mb-3">Как использовать QR-код:</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Откройте камеру на смартфоне или QR-сканер</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Направьте камеру на QR-код</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Перейдите по ссылке или получите информацию</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-lg p-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Почему выбирают наш генератор?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Создавайте профессиональные QR-коды быстро и бесплатно без регистрации
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-indigo-50 rounded-xl">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Zap className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Мгновенная генерация</h3>
              <p className="text-gray-600 text-sm">QR-код создается за доли секунды без задержек</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-xl">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Download className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Бесплатная загрузка</h3>
              <p className="text-gray-600 text-sm">Скачивайте QR-коды в формате PNG без ограничений</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-xl">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Оптимизация для печати</h3>
              <p className="text-gray-600 text-sm">Высокое разрешение для качественной печати на любых носителях</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center text-gray-600">
            <p>© {new Date().getFullYear()} Генератор QR-кодов. Все права защищены.</p>
            <p className="mt-2 text-sm">Бесплатный онлайн-инструмент для создания QR-кодов</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

